// src/components/headerone/bots/OmniBot.js
// Omni bot component - receives userData from Cognito and initializes Omni bot
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { extractUserDataFromPayload } from '../SharedHeader';

const OMNI_ORIGIN = 'https://app.demo.omnibots.ai';
const IFRAME_ID = 'iframe-id';

const wait = (ms) => new Promise(r => setTimeout(r, ms));

function getTargetOrigin(el) {
  try {
    const base = typeof document !== 'undefined' ? document.baseURI : undefined;
    return el?.src ? new URL(el.src, base).origin : OMNI_ORIGIN;
  } catch { return OMNI_ORIGIN; }
}

async function waitForIframe(intervalMs = 100, signal) {
  for (;;) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const el = document.getElementById(IFRAME_ID);
    if (el && el.contentWindow && isWidgetOpen()) return el;
    await wait(intervalMs);
  }
}

async function sendOmni(event, payload, { waitForEl = true, signal, immediate = false } = {}) {
  let el = document.getElementById(IFRAME_ID);

  // If immediate flag is set and iframe is ready, send immediately without waiting
  if (immediate && el && el.contentWindow && isWidgetOpen()) {
    const origin = getTargetOrigin(el);
    const msg = { channel: 'omnibot', event, payload, ts: Date.now() };
    el.contentWindow.postMessage(msg, origin);
    console.info('[OMNI] sent immediately', { event, origin, pageId: payload?.pageId });
    return 'sent';
  }

  if (waitForEl && (!el || !el.contentWindow || !isWidgetOpen())) {
    // Only log if we're actually waiting (not just checking)
    // Reduce verbosity - only log once per attempt
    const logKey = `omni-wait-${event}`;
    if (!window[logKey]) {
      window[logKey] = true;
      console.info('[OMNI] waiting for OPEN iframe…');
      setTimeout(() => { window[logKey] = false; }, 5000); // Reset after 5s
    }
    el = await waitForIframe(100, signal);
  }

  if (!el || !el.contentWindow || !isWidgetOpen()) {
    // Don't warn for context events when widget is closed - this is expected
    if (event !== 'context') {
      console.warn('[OMNI] iframe not open/available');
    }
    return 'missing';
  }

  await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

  const origin = getTargetOrigin(el);
  const msg = { channel: 'omnibot', event, payload, ts: Date.now() };
  el.contentWindow.postMessage(msg, origin);
  console.info('[OMNI] sent', { event, origin });
  return 'sent';
}

function isWidgetOpen() {
  const btn = document.getElementById('mainButton');
  if (!btn) return false;
  const expanded = btn.getAttribute('aria-expanded');
  if (expanded === 'true') return true;
  if (expanded === 'false') return false;
  return !btn.classList.contains('df-closed');
}

const getBrowserDetails = () => {
  const ua = navigator.userAgent || '';
  const mCriOS = ua.match(/CriOS\/([\d.]+)/);        if (mCriOS) return `Chrome iOS ${mCriOS[1]}`;
  const mFxiOS = ua.match(/FxiOS\/([\d.]+)/);        if (mFxiOS) return `Firefox iOS ${mFxiOS[1]}`;
  const mEdgiOS = ua.match(/EdgiOS\/([\d.]+)/);      if (mEdgiOS) return `Edge iOS ${mEdgiOS[1]}`;
  const mOPiOS = ua.match(/OPiOS\/([\d.]+)/);        if (mOPiOS) return `Opera iOS ${mOPiOS[1]}`;
  const mEdge  = ua.match(/Edg\/([\d.]+)/);          if (mEdge)  return `Edge ${mEdge[1]}`;
  const mOPR   = ua.match(/OPR\/([\d.]+)/);          if (mOPR)   return `Opera ${mOPR[1]}`;
  const mFirefox = ua.match(/Firefox\/([\d.]+)/);    if (mFirefox) return `Firefox ${mFirefox[1]}`;
  const mChrome  = /Chrome\/([\d.]+)/.exec(ua);      if (mChrome && !/Chromium|OPR|Edg/.test(ua)) return `Chrome ${mChrome[1]}`;
  const mSafari  = /Safari\/[\d.]+/.test(ua) && ua.match(/Version\/([\d.]+)/);
  if (mSafari) return `Safari ${mSafari[1]}`;
  const mChromium = ua.match(/Chromium\/([\d.]+)/);  if (mChromium) return `Chromium ${mChromium[1]}`;
  const brand = navigator.userAgentData?.brands?.find(b => b.brand && b.brand !== 'Chromium')
    || navigator.userAgentData?.brands?.[0];
  if (brand) return `${brand.brand} ${brand.version}`;
  return 'Unknown';
};

const getOS = () => {
  const ua = navigator.userAgent || '';
  if (/Windows NT/i.test(ua)) return 'Windows';
  if (/Macintosh|Mac OS X/i.test(ua)) return 'macOS';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
  if (/Android/i.test(ua)) return 'Android';
  if (/CrOS/i.test(ua)) return 'Chrome OS';
  if (/Linux/i.test(ua)) return 'Linux';
  return navigator.platform || 'Unknown';
};

const getScreenResolution = () => `${window.screen?.width || 0}x${window.screen?.height || 0}`;

// Global flag to track if script is loaded (shared across all Omni bot instances)
// Use window-level to share across modules
if (typeof window !== 'undefined' && !window.__omnibotScriptLoaded) {
  window.__omnibotScriptLoaded = false;
}
// Track last time we re-pushed config to prevent spam
let lastConfigPushTime = 0;
const CONFIG_PUSH_COOLDOWN = 2000; // Don't re-push more than once every 2 seconds

// Global persistent bot visibility monitor that runs independently of React
if (typeof window !== 'undefined' && !window.__omniBotVisibilityMonitor) {
  window.__omniBotVisibilityMonitor = true;
  
  let lastPathname = window.location.pathname;
  let visibilityCheckInterval = null;
  
  const ensureBotVisible = () => {
    const activeBot = document.body?.dataset?.gtsActiveBot;
    if (activeBot === 'omni' || activeBot === 'omni8x8') {
      // Ensure body attribute is set
      if (document.body.dataset.gtsActiveBot !== activeBot) {
        console.warn('[OMNI Global Monitor] Body attribute mismatch', {
          expected: activeBot,
          actual: document.body.dataset.gtsActiveBot
        });
        document.body.dataset.gtsActiveBot = activeBot;
      }
      
      // Check and restore bot elements
      const btn = document.getElementById('mainButton');
      const iframe = document.getElementById('iframe-id');
      const questionBox = document.getElementById('question-box');
      const messageBox = document.querySelector('.df-message-box');
      
      const elements = [btn, iframe, questionBox, messageBox].filter(Boolean);
      
      if (elements.length > 0) {
        // Elements exist - ensure they're visible with !important
        elements.forEach((el) => {
          const before = {
            display: window.getComputedStyle(el).display,
            visibility: window.getComputedStyle(el).visibility,
            opacity: window.getComputedStyle(el).opacity
          };
          // Always force visibility, don't check computed style first
          el.style.setProperty('display', 'block', 'important');
          el.style.setProperty('visibility', 'visible', 'important');
          el.style.setProperty('opacity', '1', 'important');
          const after = {
            display: window.getComputedStyle(el).display,
            visibility: window.getComputedStyle(el).visibility,
            opacity: window.getComputedStyle(el).opacity
          };
          if (before.display === 'none' || before.visibility === 'hidden') {
            console.warn('[OMNI Global Monitor] Fixed hidden element', {
              id: el.id,
              before,
              after
            });
          }
        });
      } else {
        // Elements don't exist - log this
        console.warn('[OMNI Global Monitor] Elements missing', {
          activeBot,
          bodyAttr: document.body?.dataset?.gtsActiveBot,
          btnExists: !!btn,
          iframeExists: !!iframe
        });
      }
    }
  };
  
  // Continuous monitoring that runs every 300ms
  visibilityCheckInterval = setInterval(ensureBotVisible, 300);
  
  // Also check on route changes by monitoring pathname
  const checkRouteChange = () => {
    const currentPathname = window.location.pathname;
    if (currentPathname !== lastPathname) {
      console.log('[OMNI Global Monitor] Route changed detected', {
        from: lastPathname,
        to: currentPathname,
        bodyAttr: document.body?.dataset?.gtsActiveBot,
        btnExists: !!document.getElementById('mainButton'),
        iframeExists: !!document.getElementById('iframe-id')
      });
      lastPathname = currentPathname;
      // Route changed - immediately ensure visibility
      setTimeout(ensureBotVisible, 0);
      setTimeout(ensureBotVisible, 50);
      setTimeout(ensureBotVisible, 200);
      setTimeout(ensureBotVisible, 500);
    }
  };
  
  // Check for route changes periodically (very frequent to catch React Router changes)
  setInterval(checkRouteChange, 50);
  
  // Store references to bot elements to prevent them from being garbage collected
  let botElementRefs = {
    btn: null,
    iframe: null,
    questionBox: null,
    messageBox: null
  };
  
  // Monitor DOM for element removal and immediately restore
  const domObserver = new MutationObserver((mutations) => {
    const activeBot = document.body?.dataset?.gtsActiveBot;
    if (activeBot === 'omni' || activeBot === 'omni8x8') {
      // If body attribute changed away from omni, restore it
      if (document.body.dataset.gtsActiveBot !== activeBot) {
        document.body.dataset.gtsActiveBot = activeBot;
      }
      
      // Check for removed nodes
      mutations.forEach((mutation) => {
        mutation.removedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            const isBotElement = node.id === 'mainButton' || 
                                 node.id === 'iframe-id' || 
                                 node.id === 'question-box' ||
                                 node.querySelector?.('#mainButton') ||
                                 node.querySelector?.('#iframe-id');
            
            if (isBotElement) {
              console.warn('[OMNI] Bot element removed during navigation!');
              // Immediately try to restore
              setTimeout(() => {
                ensureBotVisible();
                // If elements still don't exist, they might need to be recreated
                const btn = document.getElementById('mainButton');
                const iframe = document.getElementById('iframe-id');
                if (!btn && !iframe) {
                  // Elements were removed - trigger reinitialization
                  const event = new CustomEvent('omni:restoreBot', { detail: { bot: activeBot } });
                  window.dispatchEvent(event);
                }
              }, 0);
            }
          }
        });
      });
      
      ensureBotVisible();
    }
  });
  
  // Watch body for attribute changes and child removals
  if (document.body) {
    // Also watch for body attribute changes specifically
    const bodyAttrObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-gts-active-bot') {
          const newValue = document.body.dataset.gtsActiveBot;
          // Only warn when it's a real bug: Omni elements exist but body attr changed away from omni/omni8x8
          if ((newValue !== 'omni' && newValue !== 'omni8x8') &&
              (document.getElementById('mainButton') || document.getElementById('iframe-id'))) {
            console.error('[OMNI Global Monitor] Body attribute changed away from Omni but elements exist!', {
              newValue,
              btnExists: !!document.getElementById('mainButton'),
              iframeExists: !!document.getElementById('iframe-id')
            });
          }
        }
      });
    });
    
    bodyAttrObserver.observe(document.body, { 
      attributes: true, 
      attributeFilter: ['data-gts-active-bot'],
      attributeOldValue: true
    });
    
    domObserver.observe(document.body, { 
      attributes: true, 
      attributeFilter: ['data-gts-active-bot'],
      childList: true,
      subtree: true
    });
  }
  
  // Listen for restore events
  window.addEventListener('omni:restoreBot', (e) => {
    const bot = e.detail?.bot;
    if (bot === 'omni' || bot === 'omni8x8') {
      ensureBotVisible();
    }
  });
  
  // Listen for popstate (browser back/forward)
  window.addEventListener('popstate', () => {
    setTimeout(ensureBotVisible, 0);
    setTimeout(ensureBotVisible, 50);
    setTimeout(ensureBotVisible, 200);
  });
  
  // Intercept pushState and replaceState
  const originalPushState = window.history.pushState;
  const originalReplaceState = window.history.replaceState;
  
  window.history.pushState = function(...args) {
    originalPushState.apply(window.history, args);
    setTimeout(ensureBotVisible, 0);
    setTimeout(ensureBotVisible, 50);
    setTimeout(ensureBotVisible, 200);
  };
  
  window.history.replaceState = function(...args) {
    originalReplaceState.apply(window.history, args);
    setTimeout(ensureBotVisible, 0);
    setTimeout(ensureBotVisible, 50);
    setTimeout(ensureBotVisible, 200);
  };
}

function OmniBot({ userData, payload }) {
  const location = useLocation();
  const firstRunRef = useRef(true);
  const mountTimeRef = useRef(Date.now());
  
  // Log component lifecycle
  useEffect(() => {
    console.log('[OMNI] Component mounted', { mountTime: mountTimeRef.current });
    return () => {
      console.log('[OMNI] Component unmounting', {
        pathname: location.pathname,
        btnExists: !!document.getElementById('mainButton'),
        iframeExists: !!document.getElementById('iframe-id'),
        bodyAttr: document.body?.dataset?.gtsActiveBot
      });
    };
  }, []);

  // Map Cognito userData to Omni format
  // Omni expects: customerID (last 4), customerName, emailAddress
  const customerID = userData?.customerID || '';
  const customerName = userData?.fullName || '';
  const emailAddress = userData?.emailAddress || '';

  // CRITICAL: Ensure body attribute is set when this component is mounted/active
  // This must run before the bootstrap component to ensure CSS visibility rules work
  useEffect(() => {
    if (typeof document !== 'undefined' && document.body) {
      document.body.dataset.gtsActiveBot = 'omni';
      
      // Set up a persistent interval to ensure bot stays visible
      // This runs even if component unmounts/remounts
      const visibilityCheck = setInterval(() => {
        if (document.body.dataset.gtsActiveBot === 'omni') {
          const btn = document.getElementById('mainButton');
          const iframe = document.getElementById('iframe-id');
          if (btn || iframe) {
            // Bot elements exist - ensure they're visible
            [btn, iframe].forEach((el) => {
              if (el) {
                const computed = window.getComputedStyle(el);
                if (computed.display === 'none' || computed.visibility === 'hidden' || computed.opacity === '0') {
                  el.style.setProperty('display', 'block', 'important');
                  el.style.setProperty('visibility', 'visible', 'important');
                  el.style.setProperty('opacity', '1', 'important');
                }
              }
            });
          }
        }
      }, 500); // Check every 500ms
      
      return () => {
        clearInterval(visibilityCheck);
      };
    }
  }, []); // Only run once on mount

  // Also ensure it's set on route changes (defensive)
  useEffect(() => {
    if (typeof document !== 'undefined' && document.body) {
      // Set body attribute IMMEDIATELY and synchronously - CRITICAL
      document.body.dataset.gtsActiveBot = 'omni';
      
      // Lock the body attribute to prevent it from being changed
      Object.defineProperty(document.body.dataset, 'gtsActiveBot', {
        value: 'omni',
        writable: true,
        configurable: true
      });
      
      // Immediately ensure bot elements stay visible during route changes
      // This prevents the bot from disappearing during navigation
      const ensureVisibleImmediate = () => {
        // Re-set body attribute in case it was cleared
        if (document.body.dataset.gtsActiveBot !== 'omni') {
          document.body.dataset.gtsActiveBot = 'omni';
        }
        
        const btn = document.getElementById('mainButton');
        const iframe = document.getElementById('iframe-id');
        const questionBox = document.getElementById('question-box');
        const messageBox = document.querySelector('.df-message-box');
        
        // If elements don't exist, we need to reinitialize
        if (!btn && !iframe) {
          // Elements were removed - trigger reinitialization
          const scriptLoaded = document.getElementById('omnibot-launcher') !== null;
          if (scriptLoaded && window._omnibotJs && Array.isArray(window._omnibotJs)) {
            // Re-push config to recreate elements
            const now = Date.now();
            if ((now - lastConfigPushTime) > CONFIG_PUSH_COOLDOWN) {
              lastConfigPushTime = now;
              // We need to get the config from the component, but we can't access it here
              // Instead, dispatch an event that the component can listen to
              window.dispatchEvent(new CustomEvent('omni:routeChange', { 
                detail: { bot: 'omni', pathname: location.pathname } 
              }));
            }
          }
          return; // Can't do anything else if elements don't exist
        }
        
        // Remove any hiding styles immediately and force visibility
        [btn, iframe, questionBox, messageBox].forEach((el) => {
          if (el) {
            el.style.removeProperty('display');
            el.style.removeProperty('opacity');
            el.style.removeProperty('visibility');
            // Always force show with !important
            el.style.setProperty('display', 'block', 'important');
            el.style.setProperty('visibility', 'visible', 'important');
            el.style.setProperty('opacity', '1', 'important');
          }
        });
      };
      
      // Run immediately and multiple times to catch any timing issues
      ensureVisibleImmediate();
      setTimeout(ensureVisibleImmediate, 0);
      setTimeout(ensureVisibleImmediate, 10);
      setTimeout(ensureVisibleImmediate, 50);
      setTimeout(ensureVisibleImmediate, 100);
      setTimeout(ensureVisibleImmediate, 200);
      setTimeout(ensureVisibleImmediate, 500);
      
      // Also run after a microtask to catch any async hiding
      Promise.resolve().then(ensureVisibleImmediate);
      
      // And run after next frame
      requestAnimationFrame(() => {
        requestAnimationFrame(ensureVisibleImmediate);
      });
    }
  }, [location.pathname, location.search]);

  function OmnibotBootstrap({ pageId }) {
    const collect = () => ({
      subjectLine: document.title || '',
      currentURL: window.location.href,
      browserDetails: getBrowserDetails(),
      screenResolution: getScreenResolution(),
      platform: getOS(),
      customerID,
      customerName,
      emailAddress,
    });

    const waitForTitleStable = (stableMs = 350, timeoutMs = 3000) =>
      new Promise(resolve => {
        const startTitle = document.title || '';
        let lastChange = Date.now();
        let resolved = false;
        let raf1 = 0, raf2 = 0;
        let timeout = 0;
        let debounce = 0;
        let obs = null;

        const done = () => {
          if (resolved) return;
          resolved = true;
          if (obs) obs.disconnect();
          if (timeout) clearTimeout(timeout);
          if (debounce) clearTimeout(debounce);
          if (raf1) cancelAnimationFrame(raf1);
          if (raf2) cancelAnimationFrame(raf2);
          raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(resolve);
          });
        };

        const armDebounce = () => {
          if (debounce) clearTimeout(debounce);
          debounce = setTimeout(() => {
            if (Date.now() - lastChange >= stableMs) done();
          }, stableMs);
        };

        const titleEl = document.querySelector('head > title');
        if (titleEl && 'MutationObserver' in window) {
          obs = new MutationObserver(() => {
            lastChange = Date.now();
            armDebounce();
          });
          obs.observe(titleEl, { childList: true, characterData: true, subtree: true });
        }

        armDebounce();
        if (document.readyState !== 'complete') {
          window.addEventListener('load', armDebounce, { once: true });
        }

        timeout = setTimeout(done, timeoutMs);

        if ((document.title || '') !== startTitle && (document.title || '') !== '') {
          lastChange = Date.now();
          armDebounce();
        }
      });

    useEffect(() => {
      const w = window;
      w._omnibotJs = w._omnibotJs || [];

      let cancelled = false;

      const pushConfig = (updatePageId = false) => {
        // If elements exist and we're just updating pageId, don't push new config
        // This prevents the bot script from removing/recreating elements
        if (updatePageId) {
          const btn = document.getElementById('mainButton');
          const iframe = document.getElementById('iframe-id');
          if (btn || iframe) {
            // Elements exist - just update the iframe URL if needed, don't re-push config
            // The bot script should handle pageId changes without removing elements
            return;
          }
        }
        
        const config = {
          deploymentId: '9d1f3b79-9c85-4757-a4ca-5497a4505d2b',
          orgId: '9c6603a7-d5de-4f80-b5b0-6e13bfea2fe5',
          appId: '2ef241502396086a34add2327467b10b2',
          closeText: 'Virtual Assistant',
          infoText:
            "Hi, I'm the State of GTS Virtual Agent. I can answer questions, transfer to an agent and so much more.",
          language: 'en',
          userType: 'df_generic_rag_agent',
          pageId: pageId || '',
          environment: 'production',
          dataConfig: collect(),
        };
        w._omnibotJs.push(config);
      };

      const init = async () => {
        // CRITICAL: Check if elements already exist before pushing config
        // If elements exist, the bot is already initialized and we should NOT push config
        // Pushing config when elements exist causes the bot script to reinitialize and remove elements
        const existingBtn = document.getElementById('mainButton');
        const existingIframe = document.getElementById('iframe-id');
        
        // Check if elements exist AND are visible (not hidden by CSS)
        const isElementVisible = (el) => {
          if (!el) return false;
          const style = window.getComputedStyle(el);
          return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
        };
        
        const btnVisible = existingBtn && isElementVisible(existingBtn);
        const iframeVisible = existingIframe && isElementVisible(existingIframe);
        
        // Only skip if BOTH elements exist AND are visible
        if (btnVisible && iframeVisible) {
          console.log('[OMNI] Both elements exist and visible, skipping config push to prevent reinitialization', {
            btn: !!existingBtn,
            iframe: !!existingIframe,
            btnVisible,
            iframeVisible
          });
          // Elements exist and are visible - just ensure they stay visible
          [existingBtn, existingIframe].forEach((el) => {
            if (el) {
              el.style.setProperty('display', 'block', 'important');
              el.style.setProperty('visibility', 'visible', 'important');
              el.style.setProperty('opacity', '1', 'important');
            }
          });
          return; // Early return - don't push config or load script
        }
        
        // Elements are missing or hidden - log and continue to push config
        if (!existingBtn || !existingIframe) {
          console.warn('[OMNI] Elements missing, will push config to recreate them', {
            btn: !!existingBtn,
            iframe: !!existingIframe,
            btnVisible,
            iframeVisible
          });
        } else {
          console.warn('[OMNI] Elements exist but are hidden, will push config to restore them', {
            btn: !!existingBtn,
            iframe: !!existingIframe,
            btnVisible,
            iframeVisible
          });
        }
        
        await waitForTitleStable();
        if (cancelled) return;
        pushConfig();

        // Only load script once globally (shared across all Omni bot instances)
        if (!window.__omnibotScriptLoaded && !document.getElementById('omnibot-launcher')) {
          window.__omnibotScriptLoaded = true;
          const s = document.createElement('script');
          s.id = 'omnibot-launcher';
          s.type = 'text/javascript';
          s.async = true;
          s.src = 'https://app.demo.omnibots.ai/omnibot-launcher.js';
          const firstScript = document.getElementsByTagName('script')[0];
          firstScript.parentNode.insertBefore(s, firstScript);
        }
      };

      init();
      
      // Listen for route changes - but DON'T re-push config if elements exist
      // Re-pushing config causes the bot script to remove and recreate elements
      const handleRouteChange = (e) => {
        if (e.detail?.bot === 'omni') {
          // Check if elements exist
          const btn = document.getElementById('mainButton');
          const iframe = document.getElementById('iframe-id');
          if (!btn && !iframe) {
            // Elements are missing - wait a bit to see if they're being recreated
            // Only re-push if they're still missing after a delay
            setTimeout(() => {
              const btn2 = document.getElementById('mainButton');
              const iframe2 = document.getElementById('iframe-id');
              if (!btn2 && !iframe2) {
                // Elements are still missing after delay - re-push config
                const now = Date.now();
                if ((now - lastConfigPushTime) > CONFIG_PUSH_COOLDOWN) {
                  lastConfigPushTime = now;
                  pushConfig();
                  console.log('[OMNI] Route changed, elements missing, re-pushed config');
                }
              } else {
                // Elements appeared - just ensure visibility
                [btn2, iframe2].forEach((el) => {
                  if (el) {
                    el.style.setProperty('display', 'block', 'important');
                    el.style.setProperty('visibility', 'visible', 'important');
                    el.style.setProperty('opacity', '1', 'important');
                  }
                });
              }
            }, 500); // Wait 500ms before re-pushing
          } else {
            // Elements exist - just ensure visibility, DON'T re-push config
            [btn, iframe].forEach((el) => {
              if (el) {
                el.style.setProperty('display', 'block', 'important');
                el.style.setProperty('visibility', 'visible', 'important');
                el.style.setProperty('opacity', '1', 'important');
              }
            });
          }
        }
      };
      
      window.addEventListener('omni:routeChange', handleRouteChange);

      return () => {
        cancelled = true;
        window.removeEventListener('omni:routeChange', handleRouteChange);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only initialize script once

    // Ensure bot elements remain visible on route changes
    // This runs whenever the route changes to ensure the bot doesn't disappear
    useEffect(() => {
      // CRITICAL: Ensure body attribute is set to "omni" for CSS visibility rules IMMEDIATELY
      // Set this synchronously before anything else to prevent flicker
      if (typeof document !== 'undefined' && document.body) {
        document.body.dataset.gtsActiveBot = 'omni';
      }
      
      // Check if elements exist FIRST - if they do, just ensure visibility and return early
      // This prevents any re-initialization that might cause the bot script to remove elements
      const immediateBtn = document.getElementById('mainButton');
      const immediateIframe = document.getElementById('iframe-id');
      
      if (immediateBtn || immediateIframe) {
        // Elements exist - just ensure visibility, don't do anything else
        const immediateQuestionBox = document.getElementById('question-box');
        const immediateMessageBox = document.querySelector('.df-message-box');
        
        [immediateBtn, immediateIframe, immediateQuestionBox, immediateMessageBox].forEach((el) => {
          if (el) {
            el.style.setProperty('display', 'block', 'important');
            el.style.setProperty('visibility', 'visible', 'important');
            el.style.setProperty('opacity', '1', 'important');
          }
        });
        
        // Early return - don't set up observers or do anything that might trigger re-initialization
        return;
      }
      
      // Elements don't exist - continue with the rest of the logic
      const immediateQuestionBox = document.getElementById('question-box');
      const immediateMessageBox = document.querySelector('.df-message-box');
      
      // Immediately show any existing elements
      [immediateBtn, immediateIframe, immediateQuestionBox, immediateMessageBox].forEach((el) => {
        if (el) {
          el.style.removeProperty('display');
          el.style.removeProperty('opacity');
          el.style.removeProperty('visibility');
        }
      });

      // Watch for bot elements being removed from DOM and prevent hiding
      let elementObserver = null;
      let removalObserver = null;
      const btn = document.getElementById('mainButton');
      
      // Intercept removals to prevent bot elements from being removed
      if ('MutationObserver' in window) {
        removalObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            mutation.removedNodes.forEach((node) => {
              // Check if removed node is a bot element or contains bot elements
              if (node.nodeType === 1) { // Element node
                const isBotElement = node.id === 'mainButton' || 
                                     node.id === 'iframe-id' || 
                                     node.id === 'question-box' ||
                                     node.querySelector?.('#mainButton') ||
                                     node.querySelector?.('#iframe-id');
                
                if (isBotElement) {
                  console.warn('[OMNI] Bot element was removed! Attempting to restore...');
                  // Try to restore immediately
                  setTimeout(() => {
                    const scriptLoaded = document.getElementById('omnibot-launcher') !== null;
                    if (scriptLoaded && window._omnibotJs && Array.isArray(window._omnibotJs)) {
                      const now = Date.now();
                      if ((now - lastConfigPushTime) > CONFIG_PUSH_COOLDOWN) {
                        lastConfigPushTime = now;
                        const config = {
                          deploymentId: '9d1f3b79-9c85-4757-a4ca-5497a4505d2b',
                          orgId: '9c6603a7-d5de-4f80-b5b0-6e13bfea2fe5',
                          appId: '2ef241502396086a34add2327467b10b2',
                          closeText: 'Virtual Assistant',
                          infoText: "Hi, I'm the State of GTS Virtual Agent. I can answer questions, transfer to an agent and so much more.",
                          language: 'en',
                          userType: 'df_generic_rag_agent',
                          pageId: pageId || '',
                          environment: 'production',
                          dataConfig: collect(),
                        };
                        window._omnibotJs.push(config);
                        console.log('[OMNI] Re-pushed config after element removal');
                      }
                    }
                  }, 100);
                }
              }
            });
          });
        });
        
        // Watch for attribute changes that might hide elements
        if (btn) {
          elementObserver = new MutationObserver((mutations) => {
            // Check if bot elements were removed
            const currentBtn = document.getElementById('mainButton');
            const currentIframe = document.getElementById('iframe-id');
            if (!currentBtn && !currentIframe) {
              // Elements were removed - re-push config if script is loaded
              const scriptLoaded = document.getElementById('omnibot-launcher') !== null;
              const now = Date.now();
              if (scriptLoaded && window._omnibotJs && Array.isArray(window._omnibotJs) && (now - lastConfigPushTime) > CONFIG_PUSH_COOLDOWN) {
                lastConfigPushTime = now;
                const config = {
                  deploymentId: '9d1f3b79-9c85-4757-a4ca-5497a4505d2b',
                  orgId: '9c6603a7-d5de-4f80-b5b0-6e13bfea2fe5',
                  appId: '2ef241502396086a34add2327467b10b2',
                  closeText: 'Virtual Assistant',
                  infoText: "Hi, I'm the State of GTS Virtual Agent. I can answer questions, transfer to an agent and so much more.",
                  language: 'en',
                  userType: 'df_generic_rag_agent',
                  pageId: pageId || '',
                  environment: 'production',
                  dataConfig: collect(),
                };
                window._omnibotJs.push(config);
                console.log('[OMNI] Elements removed, re-pushed config');
              }
            } else {
              // Elements exist - ensure they're visible
              [currentBtn, currentIframe].forEach((el) => {
                if (el) {
                  const computed = window.getComputedStyle(el);
                  if (computed.display === 'none' || computed.visibility === 'hidden') {
                    el.style.setProperty('display', 'block', 'important');
                    el.style.setProperty('visibility', 'visible', 'important');
                    el.style.setProperty('opacity', '1', 'important');
                  }
                }
              });
            }
          });
          elementObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
        }
        
        // Watch document body for removals
        removalObserver.observe(document.body, { childList: true, subtree: true });
      }

      // Ensure the bot UI elements are visible when this component is active
      const ensureVisible = () => {
        // First, ensure body attribute is set
        if (typeof document !== 'undefined' && document.body) {
          document.body.dataset.gtsActiveBot = 'omni';
        }

        const btn = document.getElementById('mainButton');
        const iframe = document.getElementById('iframe-id');
        const questionBox = document.getElementById('question-box');
        const messageBox = document.querySelector('.df-message-box');
        
        // Check if elements exist
        const elements = [btn, iframe, questionBox, messageBox].filter(Boolean);
        
        if (elements.length === 0) {
          // Elements don't exist yet - the script might still be loading
          // OR they were removed. Try to re-push config if script is loaded
          const scriptLoaded = document.getElementById('omnibot-launcher') !== null;
          const now = Date.now();
          const shouldRepush = scriptLoaded && 
                               window._omnibotJs && 
                               Array.isArray(window._omnibotJs) &&
                               (now - lastConfigPushTime) > CONFIG_PUSH_COOLDOWN;
          
          if (shouldRepush) {
            // Script is loaded but elements are missing - re-push config (with cooldown)
            lastConfigPushTime = now;
            const config = {
              deploymentId: '9d1f3b79-9c85-4757-a4ca-5497a4505d2b',
              orgId: '9c6603a7-d5de-4f80-b5b0-6e13bfea2fe5',
              appId: '2ef241502396086a34add2327467b10b2',
              closeText: 'Virtual Assistant',
              infoText: "Hi, I'm the State of GTS Virtual Agent. I can answer questions, transfer to an agent and so much more.",
              language: 'en',
              userType: 'df_generic_rag_agent',
              pageId: pageId || '',
              environment: 'production',
              dataConfig: collect(),
            };
            window._omnibotJs.push(config);
            console.log('[OMNI] Re-pushed config to _omnibotJs array (cooldown active)');
          }
          // Don't return early - continue to check visibility even if elements don't exist yet
        }

          // Only process elements if they exist
        if (elements.length > 0) {
          // Remove any inline styles that might hide the element
          // Note: CSS !important rules will still apply, but we need body attribute set
          elements.forEach((el) => {
            if (el) {
              el.style.removeProperty('display');
              el.style.removeProperty('opacity');
              el.style.removeProperty('visibility');
            }
          });

          // Force show using !important override if needed (last resort)
          // Only do this if elements exist but are still hidden
          elements.forEach((el) => {
            const computed = window.getComputedStyle(el);
            if (computed.display === 'none' || computed.visibility === 'hidden' || computed.opacity === '0') {
              // Double-check body attribute
              if (document.body.dataset.gtsActiveBot !== 'omni') {
                document.body.dataset.gtsActiveBot = 'omni';
                console.log('[OMNI] Body attribute was wrong, fixed to "omni"');
              } else {
                // Body attribute is correct but element is still hidden
                // This might be a timing issue - CSS should handle this, but log once per element
                const logKey = `omni-hidden-${el.id || el.className}`;
                if (!window[logKey]) {
                  window[logKey] = true;
                  console.warn('[OMNI] Element exists but is hidden despite correct body attribute:', el.id || el.className);
                  // Force show as fallback
                  el.style.setProperty('display', 'block', 'important');
                  el.style.setProperty('visibility', 'visible', 'important');
                  el.style.setProperty('opacity', '1', 'important');
                  setTimeout(() => { window[logKey] = false; }, 10000); // Reset after 10s
                }
              }
            }
          });
        }
      };

      // Run immediately and after delays to ensure DOM is ready
      // This handles cases where the DOM might not be fully updated yet
      // Reduced frequency to avoid spamming re-pushes
      ensureVisible();
      const timeout1 = setTimeout(ensureVisible, 100);
      const timeout2 = setTimeout(ensureVisible, 500);
      const timeout3 = setTimeout(ensureVisible, 1500); // Give script more time to load
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(ensureVisible);
      });

      return () => {
        clearTimeout(timeout1);
        clearTimeout(timeout2);
        clearTimeout(timeout3);
        cancelAnimationFrame(raf);
        if (elementObserver) {
          elementObserver.disconnect();
        }
        if (removalObserver) {
          removalObserver.disconnect();
        }
      };
    }, [location.pathname, location.search, pageId]); // Re-run on route changes
    
    // CRITICAL: Add a separate effect that ONLY ensures visibility on route changes
    // This runs AFTER the above effect to catch any elements that might have been removed
    useEffect(() => {
      // Small delay to let any removal/restoration settle
      const timeout = setTimeout(() => {
        const btn = document.getElementById('mainButton');
        const iframe = document.getElementById('iframe-id');
        if (btn || iframe) {
          // Elements exist - ensure they're visible
          [btn, iframe].forEach((el) => {
            if (el) {
              el.style.setProperty('display', 'block', 'important');
              el.style.setProperty('visibility', 'visible', 'important');
              el.style.setProperty('opacity', '1', 'important');
            }
          });
        } else {
          // Elements don't exist - wait a bit longer before triggering restoration
          // The bot script might be in the process of creating them
          setTimeout(() => {
            const btn2 = document.getElementById('mainButton');
            const iframe2 = document.getElementById('iframe-id');
            if (!btn2 && !iframe2) {
              // Still missing after delay - trigger restoration
              window.dispatchEvent(new CustomEvent('omni:routeChange', { 
                detail: { bot: 'omni', pathname: location.pathname } 
              }));
            }
          }, 1000); // Wait 1 second before triggering restoration
        }
      }, 100);
      
      return () => clearTimeout(timeout);
    }, [location.pathname, location.search]);

    useEffect(() => {
      const ac = new AbortController();

      (async () => {
        // Check if iframe is ready
        const iframe = document.getElementById(IFRAME_ID);
        const iframeReady = iframe && iframe.contentWindow && isWidgetOpen();
        
        // If this is the first run, wait a bit for the iframe to be fully initialized
        if (firstRunRef.current) {
          await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
          firstRunRef.current = false;
        }

        // On route changes (when firstRunRef is false), we need to force a context refresh
        // The bot middleware may not handle context updates properly, so we push a new config
        // with the updated pageId to force it to refresh, but only if the widget is open
        if (!firstRunRef.current && iframeReady) {
          // Wait a moment for document.title to update
          await new Promise(r => setTimeout(r, 100));
          
          // Push a new config with updated pageId to force context refresh
          // This is safe because the widget is already open and initialized
          const w = window;
          w._omnibotJs = w._omnibotJs || [];
          
          const collect = () => ({
            subjectLine: document.title || '',
            currentURL: window.location.href,
            browserDetails: getBrowserDetails(),
            screenResolution: getScreenResolution(),
            platform: getOS(),
            customerID,
            customerName,
            emailAddress,
          });
          
          const config = {
            deploymentId: '9d1f3b79-9c85-4757-a4ca-5497a4505d2b',
            orgId: '9c6603a7-d5de-4f80-b5b0-6e13bfea2fe5',
            appId: '2ef241502396086a34add2327467b10b2',
            closeText: 'Virtual Assistant',
            infoText: "Hi, I'm the State of GTS Virtual Agent. I can answer questions, transfer to an agent and so much more.",
            language: 'en',
            userType: 'df_generic_rag_agent',
            pageId: location.pathname,
            environment: 'production',
            dataConfig: collect(),
          };
          
          console.log('[OMNI] Pushing config to force context refresh on route change', { 
            pageId: config.pageId,
            pathname: location.pathname,
            subjectLine: config.dataConfig.subjectLine
          });
          
          // Push config to force refresh - the bot script should handle this gracefully
          // when the widget is already open
          w._omnibotJs.push(config);
          
          // Also send context message via postMessage as backup
          const message = {
            type: 'context',
            pageId: location.pathname,
            subjectLine: document.title || '',
            currentURL: window.location.href,
            browserDetails: getBrowserDetails(),
            screenResolution: getScreenResolution(),
            platform: getOS(),
            customerID,
            customerName,
            emailAddress,
          };
          
          await sendOmni('context', message, { 
            waitForEl: false,
            immediate: true,
            signal: ac.signal 
          });
        } else if (iframeReady) {
          // First run - just send context normally
          const message = {
            type: 'context',
            pageId: location.pathname,
            subjectLine: document.title || '',
            currentURL: window.location.href,
            browserDetails: getBrowserDetails(),
            screenResolution: getScreenResolution(),
            platform: getOS(),
            customerID,
            customerName,
            emailAddress,
          };
          
          await sendOmni('context', message, { 
            waitForEl: false,
            immediate: true,
            signal: ac.signal 
          });
        } else {
          // Iframe not ready yet - wait for it
          const message = {
            type: 'context',
            pageId: location.pathname,
            subjectLine: document.title || '',
            currentURL: window.location.href,
            browserDetails: getBrowserDetails(),
            screenResolution: getScreenResolution(),
            platform: getOS(),
            customerID,
            customerName,
            emailAddress,
          };
          
          await sendOmni('context', message, { 
            waitForEl: true,
            immediate: false,
            signal: ac.signal 
          });
        }
      })().catch((e) => {
        if (e?.name !== 'AbortError') console.error('[OMNI] send failed', e);
      });

      return () => ac.abort();
    }, [location.pathname, location.search, customerID, customerName, emailAddress]);

    return null;
  }

  return <OmnibotBootstrap pageId={location.pathname} />;
}

export default OmniBot;

